/**
 * BulkRegistration Component
 * Requirements: 13.1, 13.2, 13.3, 13.5, 13.6, 13.9
 * Task 9.2: Bulk Registration UI
 */
import { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Chip,
  Collapse,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { bulkRegistrationApi } from '../api/bulkRegistrationApi';
import { parseCSV, usersToCSV } from '../utils/csvParser';
import type {
  BulkRegistrationUser,
  CsvValidationError,
  BulkRegistrationResult,
  UserRegistrationResult,
} from '../types/bulkRegistration.types';

interface BulkRegistrationProps {
  companyId: string;
}

type RegistrationState = 'idle' | 'preview' | 'uploading' | 'completed';

export function BulkRegistration({ companyId }: BulkRegistrationProps) {
  const [state, setState] = useState<RegistrationState>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [users, setUsers] = useState<BulkRegistrationUser[]>([]);
  const [validationErrors, setValidationErrors] = useState<CsvValidationError[]>([]);
  const [result, setResult] = useState<BulkRegistrationResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [skipPasswordReset, setSkipPasswordReset] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = useCallback(() => {
    bulkRegistrationApi.downloadTemplate();
  }, []);

  const processFile = useCallback((selectedFile: File) => {
    setApiError(null);

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      setApiError('CSV形式のファイルのみアップロード可能です');
      return;
    }

    setFile(selectedFile);

    // Read and parse file using FileReader for test compatibility
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (text) {
        const parseResult = parseCSV(text);
        setUsers(parseResult.users);
        setValidationErrors(parseResult.errors);
        setState('preview');
      }
    };
    reader.onerror = () => {
      setApiError('ファイルの読み込みに失敗しました');
    };
    reader.readAsText(selectedFile);
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        processFile(selectedFile);
      }
    },
    [processFile]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        processFile(droppedFile);
      }
    },
    [processFile]
  );

  const handleClearFile = useCallback(() => {
    setFile(null);
    setUsers([]);
    setValidationErrors([]);
    setState('idle');
    setResult(null);
    setApiError(null);
    setSkipPasswordReset(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (users.length === 0 || validationErrors.length > 0) return;

    // companyIdが空の場合はエラー
    if (!companyId || companyId.trim() === '') {
      setApiError('会社IDが設定されていません。管理者にお問い合わせください。');
      return;
    }

    // skipPasswordResetが有効な場合、全ユーザーにパスワードが設定されているか確認
    if (skipPasswordReset) {
      const usersWithoutPassword = users.filter(u => !u.password);
      if (usersWithoutPassword.length > 0) {
        setApiError('パスワードリセットをスキップする場合、全ユーザーにパスワードを設定する必要があります。CSVのpassword列を確認してください。');
        return;
      }
    }

    setState('uploading');
    setApiError(null);

    try {
      const csvData = usersToCSV(users);
      const uploadResult = await bulkRegistrationApi.uploadUsers(companyId, csvData, skipPasswordReset);
      setResult(uploadResult);
      setState('completed');
    } catch (error) {
      // エラーの詳細情報を取得
      let errorMessage = '不明なエラー';
      if (error instanceof Error) {
        errorMessage = error.message;
        // Axiosエラーの場合、レスポンスデータも確認
        const axiosError = error as any;
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.code === 'ERR_NETWORK') {
          errorMessage = 'ネットワークエラー: サーバーに接続できません';
        }
      }
      setApiError(`登録処理に失敗しました: ${errorMessage}`);
      setState('preview');
    }
  }, [companyId, users, validationErrors.length, skipPasswordReset]);

  const handleRetry = useCallback(async () => {
    if (!result?.jobId) return;

    setState('uploading');
    setApiError(null);

    try {
      const retryResult = await bulkRegistrationApi.retryFailed(result.jobId);
      setResult(retryResult);
      setState('completed');
    } catch (error) {
      setApiError(
        `再試行に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`
      );
      setState('completed');
    }
  }, [result?.jobId]);

  const handleNewUpload = useCallback(() => {
    handleClearFile();
  }, [handleClearFile]);

  const failedResults = result?.results.filter((r) => r.status === 'failed') || [];

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        ユーザー一括登録
      </Typography>

      {/* Template Download */}
      <Box mb={3}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
        >
          CSVテンプレートをダウンロード
        </Button>
      </Box>

      {/* Error Display */}
      {apiError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {apiError}
        </Alert>
      )}

      {/* File Upload Area */}
      {(state === 'idle' || state === 'preview') && (
        <>
          <Paper
            data-testid="drop-zone"
            className={isDragActive ? 'drag-active' : ''}
            sx={{
              p: 4,
              mb: 3,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              data-testid="csv-file-input"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <UploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              CSVファイルをドラッグ&ドロップ
            </Typography>
            <Typography variant="body2" color="text.secondary">
              または クリックしてファイルを選択
            </Typography>
          </Paper>

          {/* Selected File Display */}
          {file && (
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Chip label={file.name} onDelete={handleClearFile} />
              <IconButton
                aria-label="ファイルを削除"
                size="small"
                onClick={handleClearFile}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && state === 'preview' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>バリデーションエラー</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((err, index) => (
              <li key={index}>
                行 {err.row}: {err.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Preview Table */}
      {state === 'preview' && users.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            {users.length} 件のユーザーをプレビュー中
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>ユーザー名</TableCell>
                  <TableCell>名前</TableCell>
                  <TableCell>ロール</TableCell>
                  <TableCell>パスワード</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={index}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.role || 'user'}</TableCell>
                    <TableCell>{user.password ? '********' : '（未設定）'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Skip Password Reset Option */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipPasswordReset}
                  onChange={(e) => setSkipPasswordReset(e.target.checked)}
                  data-testid="skip-password-reset-checkbox"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>初回ログイン時のパスワード変更を強制しない</Typography>
                  <Tooltip title="このオプションを有効にすると、CSVで指定したパスワードがそのまま使用され、初回ログイン時にパスワード変更を求められません。CSVのpassword列に全ユーザーのパスワードを設定する必要があります。">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              }
            />
            {skipPasswordReset && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <AlertTitle>注意</AlertTitle>
                このオプションを有効にする場合、CSVの全ユーザーにパスワードを設定する必要があります。
                パスワードは8文字以上で、大文字・小文字・数字・特殊文字を含む必要があります。
              </Alert>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={validationErrors.length > 0 || users.length === 0}
            >
              登録実行
            </Button>
          </Box>
        </>
      )}

      {/* Uploading State */}
      {state === 'uploading' && (
        <Box textAlign="center" py={4}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            登録中...
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Box>
      )}

      {/* Results Display */}
      {state === 'completed' && result && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            登録結果
          </Typography>

          <Box display="flex" gap={4} mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <SuccessIcon color="success" />
              <Typography>成功: {result.successCount} 件</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <ErrorIcon color="error" />
              <Typography>失敗: {result.failedCount} 件</Typography>
            </Box>
          </Box>

          {/* Error Details */}
          {failedResults.length > 0 && (
            <>
              <Button
                onClick={() => setShowErrorDetails(!showErrorDetails)}
                endIcon={showErrorDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 2 }}
              >
                エラー詳細
              </Button>
              <Collapse in={showErrorDetails}>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>メールアドレス</TableCell>
                        <TableCell>エラー</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedResults.map((r, index) => (
                        <TableRow key={index}>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>{r.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
                sx={{ mr: 2 }}
              >
                失敗したユーザーを再試行
              </Button>
            </>
          )}

          <Button variant="contained" onClick={handleNewUpload}>
            新しいファイルをアップロード
          </Button>
        </Paper>
      )}
    </Box>
  );
}
